import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clientAPI } from '../../services/apiServices';
import { useFetch } from '../../hooks/useFetch';
import { useFeedback } from '../../contexts/FeedbackContext';
import { Alert, EmptyState, Spinner } from '../../components/common/UI';
import {
  BriefcaseIcon,
  BuildingIcon,
  EditIcon,
  FilterIcon,
  MailIcon,
  MoreVerticalIcon,
  PhoneIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
  UserIcon,
} from '../../components/common/Icons';
import AuthenticatedImage from '../../components/common/AuthenticatedImage';

const TYPE_CLASSES = {
  business: 'bg-emerald-50 text-emerald-700',
  involved: 'bg-amber-50 text-amber-700',
  both: 'bg-sky-50 text-sky-700',
};

const isBusinessType = (type) => type === 'business' || type === 'both';

const ClientListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { confirm, notify } = useFeedback();
  const { data: clients, loading, error, refetch } = useFetch(() => clientAPI.getAll());
  const [search, setSearch] = useState('');
  const [actionClient, setActionClient] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all');

  const activityFields = [...new Set((clients || []).map((client) => client.activityField).filter(Boolean))];
  const filtered = clients?.filter((client) => {
    const needle = search.toLowerCase();
    const type = client.clientType || 'business';
    return (
      (String(client.name || '').toLowerCase().includes(needle) ||
        String(client.document || '').toLowerCase().includes(needle) ||
        String(client.email || '').toLowerCase().includes(needle) ||
        String(client.activityField || '').toLowerCase().includes(needle)) &&
      (typeFilter === 'all' || type === typeFilter) &&
      (activityFilter === 'all' || client.activityField === activityFilter)
    );
  }) || [];

  if (loading) return <Spinner size="lg" />;

  const closeActions = () => setActionClient(null);

  const handleEdit = (client) => {
    closeActions();
    navigate(`/clients/${client.id}/edit`);
  };

  const handleDelete = async (client) => {
    closeActions();
    const ok = await confirm({
      variant: 'danger',
      title: t('contacts.deleteTitle'),
      message: t('contacts.deleteMessage', { name: client.name }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
    });
    if (!ok) return;
    try {
      await clientAPI.remove(client.id);
      await refetch();
    } catch {
      notify({ variant: 'danger', title: t('contacts.deleteErrorTitle'), message: t('contacts.deleteError') });
    }
  };

  return (
    <div className="mx-auto max-w-[1104px]">
      <div className="mb-7 flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{t('navigation.contacts')}</h1>
          <p className="mt-1 text-base text-slate-600">{t('contacts.subtitle')}</p>
        </div>
        <Link
          to="/clients/new"
          className="inline-flex h-10 items-center justify-center gap-3 rounded-lg bg-sky-500 px-6 text-sm font-bold text-white shadow-soft transition hover:bg-sky-600"
        >
          <PlusIcon className="h-4 w-4" />
          {t('contacts.new')}
        </Link>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="mb-6 flex flex-col gap-4 md:flex-row">
        <label className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
          <input
            className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-12 pr-4 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-500 focus:border-sky-300 focus:ring-4 focus:ring-sky-100"
            placeholder={t('contacts.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <button
          className="inline-flex h-10 items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white px-6 text-sm font-bold text-slate-900 shadow-sm transition hover:border-sky-200 hover:text-sky-600"
          type="button"
          onClick={() => setFiltersOpen((value) => !value)}
        >
          <FilterIcon className="h-4 w-4" />
          {t('actions.filter')}
        </button>
      </div>

      {filtersOpen && (
        <section className="mb-6 grid gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-soft md:grid-cols-3">
          <label className="text-xs font-bold text-slate-600">
            {t('common.type')}
            <select className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
              <option value="all">{t('filters.all')}</option>
              {['business', 'involved', 'both'].map((type) => <option key={type} value={type}>{t(`contactTypes.${type}`)}</option>)}
            </select>
          </label>
          <label className="text-xs font-bold text-slate-600">
            {t('clientForm.activityFieldLabel')}
            <select className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm" value={activityFilter} onChange={(event) => setActivityFilter(event.target.value)}>
              <option value="all">{t('filters.all')}</option>
              {activityFields.map((field) => <option key={field} value={field}>{field}</option>)}
            </select>
          </label>
          <button
            className="mt-5 h-10 rounded-lg border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700"
            type="button"
            onClick={() => { setTypeFilter('all'); setActivityFilter('all'); }}
          >
            {t('filters.clear')}
          </button>
        </section>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          title={t('contacts.emptyTitle')}
          description={t('contacts.emptyDescription')}
          action={
            <Link
              to="/clients/new"
              className="inline-flex h-10 items-center rounded-lg bg-sky-500 px-4 text-sm font-bold text-white"
            >
              {t('contacts.create')}
            </Link>
          }
        />
      ) : (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((client) => {
            const type = client.clientType || 'business';
            return (
              <div
                key={client.id}
                className="group relative cursor-pointer rounded-lg border border-slate-200 bg-white p-5 shadow-soft transition hover:-translate-y-0.5 hover:shadow-lg"
                onClick={() => navigate(`/clients/${client.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') navigate(`/clients/${client.id}`);
                }}
              >
                <div className="mb-4 flex items-start justify-between gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-50 text-sky-500">
                    {client.avatar ? (
                      <AuthenticatedImage source={client.avatar} alt="" className="h-full w-full rounded-full object-cover" />
                    ) : isBusinessType(type) ? (
                      <BuildingIcon className="h-7 w-7" />
                    ) : (
                      <UserIcon className="h-7 w-7" />
                    )}
                  </div>
                  <button
                    className="rounded-full p-1 text-slate-600 transition hover:bg-slate-100"
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setActionClient(actionClient?.id === client.id ? null : client);
                    }}
                    aria-label={t('actions.moreOptions')}
                  >
                    <MoreVerticalIcon className="h-5 w-5" />
                  </button>
                </div>

                {actionClient?.id === client.id && (
                  <div
                    className="absolute right-5 top-12 z-10 w-40 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <button
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                      type="button"
                      onClick={() => handleEdit(client)}
                    >
                      <EditIcon className="h-4 w-4" />
                      {t('common.edit')}
                    </button>
                    <button
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-red-500 transition hover:bg-red-50"
                      type="button"
                      onClick={() => handleDelete(client)}
                    >
                      <TrashIcon className="h-4 w-4" />
                      {t('common.delete')}
                    </button>
                  </div>
                )}

                <h2 className="truncate text-base font-bold leading-snug text-slate-900">{client.name}</h2>
                <p className="mt-1 truncate text-sm text-slate-700">
                  {client.document || t('contacts.documentMissing')}
                </p>

                <div className="mt-4 space-y-2 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <MailIcon className="h-4 w-4 shrink-0 text-slate-700" />
                    <span className="truncate">{client.email || t('contacts.emailMissing')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PhoneIcon className="h-4 w-4 shrink-0 text-slate-700" />
                    <span>{client.phone || client.whatsapp || t('contacts.phoneMissing')}</span>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between gap-3">
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${TYPE_CLASSES[type] || TYPE_CLASSES.business}`}>
                    {t(`contactTypes.${type}`, type)}
                  </span>
                  {!!client.businesses?.length && (
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-slate-700">
                      <BriefcaseIcon className="h-4 w-4" />
                      {client.businesses.length}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientListPage;
